// GlobalTableContainer.js

import { GlobalTableData } from "./GlobalTableData";
import debug from "debug";
const log = debug("app:globalTable");

/**
 * Manages the global table container UI, including search functionality, pagination, and drag-and-drop behavior.
 * @category Components
 * @subcategory GlobalTable
 */
export class GlobalTableContainer {
  /**
   * Creates an instance of GlobalTableContainer.
   * @param {Object} experience - The experience object containing shared resources.
   * @param {Object} experience.eventHub - The event hub for emitting and handling application-wide events.
   */
  constructor(experience) {
    this.experience = experience;
    /**
     * Event hub for managing global table-related events.
     * @type {Object}
     */
    this.eventHub = experience.eventHub;

    /**
     * The current page in the pagination system.
     * @type {number}
     */
    this.currentPage = 1;

    /**
     * Indicates whether the modal is currently open.
     * @type {boolean}
     */
    this.isModalOpen = false;

    /**
     * Indicates whether there are more pages available.
     * @type {boolean}
     */
    this.hasMorePages = false;

    this.init();
    this.setupEventListeners();
  }

  /**
   * Sets up event listeners for global search and other related functionalities.
   */
  setupEventListeners() {
    this.eventHub.on("globalSearch", () => {
      this.toggleGlobalTableContainer();
    });

    // Listen for pagination updates from GlobalTableData
    this.eventHub.on("globalTablePaginationUpdate", (data) => {
      this.hasMorePages = data.hasMorePages;
      this.updatePaginationButtons(data.currentPage, data.hasMorePages, data.hasData);
    });
  }

  cleanup() {
    console.log("GlobalTableContainer cleanup started...");
  
    // 1. Remove event listeners from eventHub
    this.eventHub.off("globalSearch", this.toggleGlobalTableContainer);
  
    // 2. Remove pagination event listeners
    if (this.prevPageButton) {
      this.prevPageButton.removeEventListener("click", this.handlePrevPage);
    }
  
    if (this.nextPageButton) {
      this.nextPageButton.removeEventListener("click", this.handleNextPage);
    }
  
    if (this.currentPageInput) {
      this.currentPageInput.removeEventListener("change", this.handlePageChange);
    }
  
    // 3. Remove search input event listener
    if (this.searchResultsText) {
      this.searchResultsText.removeEventListener("input", this.handleSearchInput);
    }
  
    // 4. Cleanup GlobalTableData instance
    if (this.globalTableData && typeof this.globalTableData.cleanup === "function") {
      this.globalTableData.cleanup();
    }
  
    // 5. Remove draggable event listeners
    if (this.globalTableContainer) {
      this.globalTableContainer.remove();
    }
  
    // 6. Clear object references
    this.globalTableData = null;
    this.searchResultsText = null;
    this.currentPageInput = null;
    this.prevPageButton = null;
    this.nextPageButton = null;
    this.globalTableContainer = null;
  
    // 7. Reset state variables
    this.currentPage = 1;
    this.isModalOpen = false;
  
    console.log("GlobalTableContainer cleanup completed.");
  }

  /**
   * Initializes the global table container UI, including header, pagination, and search input.
   */
  init() {
    const globalTableContainer = document.createElement("div");
    globalTableContainer.classList.add("globalTableContainer");

    // Header
    const header = document.createElement("div");
    header.classList.add("globalTableHeader");

    // Search input
    this.searchResultsText = document.createElement("input");
    this.searchResultsText.id = "searchResultsText";
    this.searchResultsText.classList.add("search-results-text");
    this.searchResultsText.placeholder = "Search";

    // Close button
    const closeGlobalTable = document.createElement("img");
    closeGlobalTable.classList.add("close-global-table");
    closeGlobalTable.setAttribute("src", "./imgs/Close.png");

    // Pagination
    const paginationContainer = document.createElement("div");
    paginationContainer.id = "pagination";
    paginationContainer.className = "paginationTable";

    this.prevPageButton = document.createElement("img");
    this.prevPageButton.classList.add("prev-page-btn");
    this.prevPageButton.id = "prevPage";
    this.prevPageButton.setAttribute("src", "./imgs/chevron-left.svg");

    this.currentPageInput = document.createElement("input");
    this.currentPageInput.classList.add("current-page-input");
    this.currentPageInput.id = "currentPage";
    this.currentPageInput.type = "number";
    this.currentPageInput.value = 1;
    this.currentPageInput.min = 1;

    this.nextPageButton = document.createElement("img");
    this.nextPageButton.classList.add("next-page-btn");
    this.nextPageButton.id = "nextPage";
    this.nextPageButton.setAttribute("src", "./imgs/chevron-right.svg");

    paginationContainer.appendChild(this.prevPageButton);
    paginationContainer.appendChild(this.currentPageInput);
    paginationContainer.appendChild(this.nextPageButton);

    // Add event listeners for pagination
    this.prevPageButton.addEventListener("click", () => {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
        this.currentPageInput.value = this.currentPage;
        this.globalTableData.currentPage = this.currentPage;
        this.clearSearchAndFetchData();
      }
    });

    this.nextPageButton.addEventListener("click", () => {
      // Only allow navigation if there are more pages available
      if (this.hasMorePages) {
        this.currentPage += 1;
        this.currentPageInput.value = this.currentPage;
        this.globalTableData.currentPage = this.currentPage;
        this.clearSearchAndFetchData();
      }
    });

    this.currentPageInput.addEventListener("change", () => {
      const newPage = parseInt(this.currentPageInput.value, 10);
      if (newPage > 0 && newPage >= 1) {
        // Don't allow going to a page beyond what's available
        // If we don't have more pages and trying to go beyond current, reset to current
        if (!this.hasMorePages && newPage > this.currentPage) {
          this.currentPageInput.value = this.currentPage;
          return;
        }
        this.currentPage = newPage;
        this.globalTableData.currentPage = this.currentPage;
        this.clearSearchAndFetchData();
      }
    });

    closeGlobalTable.addEventListener("click", () => {
      this.hideGlobalTableContainer();
    });

    // Append elements to the container
    if (!document.querySelector(".globalTableContainer")) {
      document.body.appendChild(globalTableContainer);
      globalTableContainer.appendChild(header);
      globalTableContainer.appendChild(this.searchResultsText);
      globalTableContainer.appendChild(paginationContainer);
      globalTableContainer.appendChild(closeGlobalTable);
    }

    this.initDraggable(globalTableContainer, header);

    // Initialize GlobalTableData with the necessary elements
    this.globalTableData = new GlobalTableData(
      globalTableContainer,
      this.currentPageInput,
      this.eventHub,
      this.experience
    );

    // Initialize pagination button states
    this.updatePaginationButtons(this.currentPage, false, false);

    // Add event listener to search input
    this.searchResultsText.addEventListener("input", (event) => {
      const searchBy = event.target.value;
      this.currentPage = 1; // Reset to first page on new search
      this.globalTableData.currentPage = this.currentPage;
      this.currentPageInput.value = this.currentPage;
      this.hasMorePages = false; // Reset until we fetch new data
      this.updatePaginationButtons(this.currentPage, false, false);
      this.globalTableData.fetchDataAndUpdateTable(searchBy);
    });
  }

  /**
   * Clears the search field and fetches data for the current page.
   */
  clearSearchAndFetchData() {
    this.globalTableData.fetchDataAndUpdateTable(this.searchResultsText.value);
  }

  /**
   * Updates the pagination buttons' enabled/disabled state and visual appearance.
   * @param {number} currentPage - The current page number.
   * @param {boolean} hasMorePages - Whether there are more pages available.
   * @param {boolean} hasData - Whether the current page has data.
   */
  updatePaginationButtons(currentPage, hasMorePages, hasData) {
    // Update previous button: disabled on page 1
    const canGoPrev = currentPage > 1;
    if (this.prevPageButton) {
      this.prevPageButton.style.opacity = canGoPrev ? "1" : "0.5";
      this.prevPageButton.style.cursor = canGoPrev ? "pointer" : "not-allowed";
      this.prevPageButton.style.pointerEvents = canGoPrev ? "auto" : "none";
    }

    // Update next button: disabled if no more pages or no data
    const canGoNext = hasMorePages && hasData;
    if (this.nextPageButton) {
      this.nextPageButton.style.opacity = canGoNext ? "1" : "0.5";
      this.nextPageButton.style.cursor = canGoNext ? "pointer" : "not-allowed";
      this.nextPageButton.style.pointerEvents = canGoNext ? "auto" : "none";
    }
  }

  /**
   * Toggles the visibility of the global table container.
   * Emits events to lock or unlock interactions with other UI components.
   */
  toggleGlobalTableContainer() {
    this.eventHub.emit("lockEvents", true);
    const globalTableContainer = document.querySelector(".globalTableContainer");
    const dragTableContainer = document.querySelector(".drag-table-container");
    const dragTable = document.querySelector(".drag-table");

    if (dragTableContainer && dragTableContainer.style.display === "block") {
        dragTableContainer.style.display = "none";
    }

    if (dragTable && dragTable.style.display === "block") {
        dragTable.style.display = "none";
    }

    if (globalTableContainer) {
        if (globalTableContainer.style.display === "none" || !globalTableContainer.style.display) {
            globalTableContainer.style.display = "flex";
            this.globalTableData.fetchDataAndUpdateTable();
            log("Emitted: modalIsOpened");
            this.isModalOpen = true;

            // Focus the search input when the table is opened
            setTimeout(() => {
                this.searchResultsText.focus();
            }, 100);
        } else {
            globalTableContainer.style.display = "none";
            this.isModalOpen = false;
            this.eventHub.emit("lockEvents", false);
            log("Emitted: modalIsClosed");
        }
    }
}


  /**
   * Hides the global table container and clears the search field.
   */
  hideGlobalTableContainer() {
    const globalTableContainer = document.querySelector(
      ".globalTableContainer"
    );
    if (globalTableContainer && this.isModalOpen) {
      globalTableContainer.style.display = "none";
      this.searchResultsText.value = ""; // Clear the input field
      this.eventHub.emit("lockEvents", false);
      log("Emitted: modalIsClosed");
      this.isModalOpen = false;
    }
  }

  /**
   * Adds drag-and-drop functionality to the global table container.
   * @param {HTMLElement} element - The element to make draggable.
   * @param {HTMLElement} handle - The handle used to drag the element.
   */
  initDraggable(element, handle) {
    let offsetX, offsetY;

    const handleDrag = (event) => {
      event.preventDefault();
      const x = event.clientX - offsetX;
      const y = event.clientY - offsetY;
      element.style.left = `${x}px`;
      element.style.top = `${y}px`;
    };

    const handleDrop = () => {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", handleDrop);
    };

    handle.addEventListener("mousedown", (event) => {
      event.preventDefault();
      offsetX = event.clientX - element.getBoundingClientRect().left;
      offsetY = event.clientY - element.getBoundingClientRect().top;
      document.addEventListener("mousemove", handleDrag);
      document.addEventListener("mouseup", handleDrop);
    });
  }
}
